import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Youtube, Radio } from "lucide-react";
import { MainLayout } from "@/components/layout/MainLayout";
import { ActionCard, CategoryType } from "@/components/ui/action-card";
import { useTranslation } from "@/hooks/useTranslation";
import { Helmet } from "react-helmet";
import { LucideIcon } from "lucide-react";
import { ICON_MAP } from "@/components/ui/LucideIconPicker";
import { loadNavigationBoard, NavigationItem } from "@/lib/navigationBoardTypes";
import { useFeatureFilteredItems } from "@/hooks/useFeatureFilteredItems";
import { getNavigationColorClasses } from "@/components/communication/NavigationColorPicker";

interface MenuItem {
  key: string;
  labelKey: string;
  labelFallback: string;
  icon: LucideIcon;
  action: string;
  category: CategoryType;
  colorId: string;
}

const FALLBACK_ICONS: Record<string, LucideIcon> = { Youtube, Radio };

function toMenuItem(item: NavigationItem, lang: string): MenuItem {
  const IconComponent = ICON_MAP[item.iconName] || FALLBACK_ICONS[item.iconName] || Youtube;
  return {
    key: item.key,
    labelKey: item.key,
    labelFallback: lang === "he" ? item.labelHe : item.labelEn,
    icon: IconComponent,
    action: item.action,
    category: item.color as CategoryType,
    colorId: item.color,
  };
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const Entertainment = () => {
  const navigate = useNavigate();
  const { t, dir, isRTL, lang } = useTranslation();
  const [rawMenuItems, setRawMenuItems] = useState<MenuItem[]>([]);

  // Load customized items -- reload on mount and when returning from editor
  useEffect(() => {
    const loadItems = () => {
      const board = loadNavigationBoard("entertainment");
      const items = board.items
        .filter(item => (item.key === "youtube" || item.key === "radio") && item.visible !== false)
        .sort((a, b) => a.order - b.order)
        .map(item => toMenuItem(item, lang));
      setRawMenuItems(items);
    };
    loadItems();

    const handleFocus = () => loadItems();
    window.addEventListener('focus', handleFocus);
    window.addEventListener('navigationBoardUpdated', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('navigationBoardUpdated', handleFocus);
    };
  }, [lang]);

  const menuItems = useFeatureFilteredItems(rawMenuItems);

  const getLabel = (item: MenuItem) => {
    const translated = t(item.labelKey as keyof typeof t);
    return translated !== item.labelKey ? translated : item.labelFallback;
  };

  // Two-item square sizing -- same logic as HomeGrid
  const itemSize = 'min(calc((100vw - 6rem) / 2 - 1rem), calc(100vh - 12rem), 75dvh)';

  return (
    <>
      <Helmet>
        <title>{isRTL ? 'בידור' : 'Media'} - My EyeControl</title>
        <meta name="description" content="Entertainment options including YouTube and Radio" />
      </Helmet>

      <MainLayout>
        <div className="flex-1 flex flex-col h-full overflow-hidden p-4" dir={dir}>
          <div className="flex items-center mb-6 w-full justify-start">
            <h1 className="text-2xl font-bold text-foreground">
              {isRTL ? 'בידור' : 'Media'}
            </h1>
          </div>

          <div className="flex-1 flex items-center justify-center h-full">
            <motion.div
              key="entertainment-grid"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="flex flex-row items-center justify-center gap-4"
            >
              {menuItems.map((item) => {
                const colorClasses = getNavigationColorClasses(item.colorId);
                return (
                  <motion.div
                    key={`${item.key}-${item.colorId}`}
                    variants={itemVariants}
                    data-scan-item
                    style={{ width: itemSize, height: itemSize, flexShrink: 0 }}
                  >
                    <ActionCard
                      icon={item.icon}
                      label={getLabel(item)}
                      onClick={() => navigate(item.action)}
                      category={item.category}
                      size="large"
                      borderHex={colorClasses.hex}
                      className="w-full h-full"
                    />
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </MainLayout>
    </>
  );
};

export default Entertainment;
